import _defaultTo from "lodash/defaultTo";
import _isEmpty from "lodash/isEmpty";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

import getEnv from "./getEnv";

let _appPrefersImpersonator = false;

/**
 * Returns the auth URL where the user can authenticate themselves via OAuth.
 *
 * @returns {string} The URL where the user can authenticate.
 */
export function getOAuthUrl() {
	return getEnv( "AUTH_URL", "https://my.yoast.test/auth/yoast" );
}

/**
 * Gets the domain to track auth cookies on.
 *
 * @returns {string} The cookie domain.
 */
export function getCookieDomain() {
	const cookieDomain = process.env.REACT_APP_SHARED_COOKIE_DOMAIN;

	if ( cookieDomain ) {
		return cookieDomain;
	}

	return process.env.NODE_ENV === "development" ? ".yoast.test" : ".yoast.com";
}

/**
 * Returns a cookie name with an optional environment suffix.
 * Uses REACT_APP_COOKIE_ENV_PREFIX to namespace cookies per environment (e.g., staging-platform).
 * This prevents cookie conflicts when running multiple staging environments in the same browser.
 *
 * @param {string} baseName The base cookie name, e.g., "access_token".
 *
 * @returns {string} The cookie name, e.g., "access_token" or "access_token__staging_platform".
 */
function getEnvironmentCookieName( baseName ) {
	const prefix = process.env.REACT_APP_COOKIE_ENV_PREFIX;
	if ( prefix ) {
		const sanitized = prefix
			.toLowerCase()
			.replace( /[^a-z0-9_-]/g, "_" )
			.replace( /_{2,}/g, "_" )
			.replace( /^_|_$/g, "" );
		if ( sanitized ) {
			return `${ baseName }__${ sanitized }`;
		}
	}
	return baseName;
}

/**
 * Gets the cookie name for the access token.
 *
 * @returns {string} The cookie name, e.g., "access_token" or "access_token__staging_platform".
 */
export function getAccessTokenCookieName() {
	return getEnvironmentCookieName( "access_token" );
}

/**
 * Gets the cookie name for the intended destination.
 *
 * @returns {string} The cookie name, e.g., "intendedDestination" or "intendedDestination__staging_platform".
 */
export function getIntendedDestinationCookieName() {
	return getEnvironmentCookieName( "intendedDestination" );
}

/**
 * Checks whether the intendedDestination cookie is set.
 *
 * @returns {boolean} Whether or not the cookie was set.
 */
export function hasPeriLoginCookie() {
	return !! Cookies.get( getIntendedDestinationCookieName() );
}

/**
 * Sets the intendedDestination in the site-wide cookie containing the URL where the user intended to go.
 *
 * @returns {void}
 */
export function setPeriLoginCookie() {
	if ( hasPeriLoginCookie() !== false ) {
		return;
	}

	let intendedDestination;
	try {
		intendedDestination = new URL( window.location.href );
	} catch ( e ) {
		return;
	}

	if ( intendedDestination.hash.startsWith( "#utm_" ) ) {
		intendedDestination.hash = "";
	}
	intendedDestination.searchParams.delete( "utm_source" );
	intendedDestination.searchParams.delete( "utm_medium" );
	intendedDestination.searchParams.delete( "utm_campaign" );
	intendedDestination.searchParams.delete( "utm_term" );
	intendedDestination.searchParams.delete( "utm_content" );

	Cookies.set( getIntendedDestinationCookieName(), intendedDestination.href, { domain: getCookieDomain(), path: "/" } );
}

/**
 * Saves the redirect_to query parameter in the target destination cookie.
 *
 * @param {String} intendedDestination The intended destination.
 *
 * @returns {void}
 */
export function saveIntendedDestination( intendedDestination ) {
	Cookies.set( getIntendedDestinationCookieName(), intendedDestination, { domain: getCookieDomain(), path: "/" } );
}

/**
 * Removes the intendedDestination from the site-wide cookie.
 *
 * @returns {void}
 */
export function removePeriLoginCookie() {
	Cookies.remove( getIntendedDestinationCookieName(), { domain: getCookieDomain(), path: "/" } );
}

/**
 * Redirects to the authentication URL.
 *
 * @returns {void}
 */
export function redirectToOAuthUrl() {
	setPeriLoginCookie();
	document.location.href = getOAuthUrl();
}

/**
 * Redirect the user to the login page while saving the current page as the intended destination after a successful login.
 *
 * @returns {void}
 */
export function redirectToLoginPage() {
	removePeriLoginCookie();
	setPeriLoginCookie();
	window.location.href = getEnv( "LOGIN_URL", "https://my.yoast.test/login" );
}

/**
 * Checks whether the redirect url is safe.
 *
 * @param {string} redirectUrl   The redirect url retrieved from the cookie.
 *
 * @returns {string}             The url the user should be redirected to.
 */
export function sanitizeRedirectUrl( redirectUrl ) {
	let redirectHostname;
	try {
		const parsedRedirectUrl = new URL( redirectUrl );
		redirectHostname = parsedRedirectUrl.hostname;
	} catch ( e ) {
		console.warn( "redirectURL is not a valid URL", redirectUrl );
	}

	if ( ! redirectHostname ) {
		return getEnv( "HOME_URL", "https://my.yoast.test" );
	}

	const allowedPostfixes = [
		"yoast.com",
		"yoast.test",
	];

	const isInWhitelist = allowedPostfixes.some( postfix => redirectHostname.endsWith( postfix ) );
	return isInWhitelist ? redirectUrl : getEnv( "HOME_URL", "https://my.yoast.test" );
}

/**
 * Gets the url the user should be redirected to after the login.
 *
 * @returns {string} The url the user should be redirected to.
 */
export function getRedirectUrl() {
	if ( hasPeriLoginCookie() ) {
		return sanitizeRedirectUrl( Cookies.get( getIntendedDestinationCookieName() ) );
	}
	return getEnv( "HOME_URL", "https://my.yoast.test" );
}

/**
 * Returns the URL where the user can reset their password.
 *
 * @returns {string} The URL where the user can reset their password.
 */
export function getPasswordResetUrl() {
	return getEnv( "PASSWORD_RESET_URL", "https://yoast.test/wp-login.php?action=lostpassword" );
}

/**
 * Determines whether or an access token is available.
 *
 * @returns {boolean} Whether or not an access token is available.
 */
export function hasAccessToken() {
	return !! Cookies.get( getAccessTokenCookieName() );
}

/**
 * Sets whether the app prefers to perform actions on behalf of the impersonator (opposed to the impersonated user).
 *
 * @param {boolean} appShouldPreferImpersonator the impersonator should be preferred for this app.
 *
 * @returns {void}
 */
export function setAppImpersonatorPreference( appShouldPreferImpersonator ) {
	_appPrefersImpersonator = !! appShouldPreferImpersonator;
}

/**
 * Checks whether the app prefers to perform actions on behalf of the impersonator (opposed to the impersonated user).
 *
 * @returns {boolean} True if the impersonator should have preference.
 */
export function appPrefersImpersonator() {
	return !! _appPrefersImpersonator;
}

/**
 * Returns the access token known from the cookies.
 *
 * @param {boolean} preferImpersonator If a user is impersonated, setting this to true will yield the impersonator.
 *
 * @returns {string} The available access token.
 */
export function getAccessToken( preferImpersonator = false ) {
	const accessToken = _defaultTo( Cookies.get( getAccessTokenCookieName() ), "" );
	if ( ! preferImpersonator ) {
		return accessToken;
	}

	try {
		const decoded = jwtDecode( accessToken );
		return decoded.impersonatorToken || accessToken;
	} catch ( e ) {
		return accessToken;
	}
}

/**
 * Gets a list of role names of the current user.
 *
 * @param {boolean} preferImpersonator If a user is impersonated, setting this to true will yield the impersonator.
 *
 * @returns {string[]} The list of role names that the user has.
 */
export function getUserRoles( preferImpersonator = false ) {
	const accessToken = getAccessToken( preferImpersonator );

	if ( ! accessToken ) {
		return [];
	}

	try {
		const decoded = jwtDecode( accessToken );
		return decoded.roles || [];
	} catch ( e ) {
		return [];
	}
}

/**
 * Returns the user ID from the JWT in the access_token cookie.
 *
 * @param {boolean} preferImpersonator If a user is impersonated, setting this to true will yield the impersonator.
 *
 * @returns {string} The known user ID, or empty if unset.
 */
export function getUserId( preferImpersonator = false ) {
	const accessToken = getAccessToken( preferImpersonator );

	if ( ! accessToken ) {
		return "";
	}

	try {
		const decoded = jwtDecode( accessToken );
		return decoded.accountId;
	} catch ( e ) {
		return "";
	}
}

/**
 * Checks whether the access token has a valid JWT payload.
 *
 * @returns {boolean} True if the access token has a valid JWT payload, otherwise false.
 */
export function hasMalformedAccessToken() {
	try {
		jwtDecode( getAccessToken() );
		return false;
	} catch ( e ) {
		return true;
	}
}

/**
 * Removes cookies that are relevant for authentication
 *
 * @returns {void}
 */
export function removeCookies() {
	Cookies.remove( getAccessTokenCookieName(), { domain: getCookieDomain(), path: "/" } );
}

/**
 * Sets the access token auth cookie.
 *
 * @param {string} accessToken The token to set.
 *
 * @returns {void}
 */
export function setAuthCookie( accessToken ) {
	Cookies.set( getAccessTokenCookieName(), accessToken, { domain: getCookieDomain(), path: "/" } );
}

/**
 * Stops any running impersonation session and logs in the impersonator again.
 *
 * @returns {string} The access token of the impersonator.
 */
export function cancelImpersonation() {
	const accessToken = getAccessToken( true );
	removeCookies();
	setAuthCookie( accessToken );
	return accessToken;
}

/**
 * Get the yoast.com access OAuth token of a user who is already logged in on yoast.com
 *
 * @returns {Object} A promise containing the access token.
 */
export function fetchAccessToken() {
	return new Promise( ( resolve, reject ) => {
		if ( hasAccessToken() ) {
			return resolve( getAccessToken() );
		}

		const frame     = document.createElement( "IFrame" );
		frame.onload    = () => {
			const accessToken = getAccessToken();

			/**
			 * This means that the openId callback ( nestjs-server/src/controllers/login/YoastComOpenIdController.ts:68 )
			 * did not include an access token cookie.
			 */
			if ( _isEmpty( accessToken ) ) {
				reject( new Error( "We were not able to log you in, please try again a little bit later." ) );
				return;
			}

			resolve( accessToken );
		};
		frame.src       = getOAuthUrl();
		frame.className = "auth_frame";
		document.body.appendChild( frame );
	} );
}
